import { BsThreeDots } from "react-icons/bs";
import { FaCloudDownloadAlt, FaFileExcel } from "react-icons/fa"; // Added FaFileExcel icon
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { DropdownButton, Dropdown } from "react-bootstrap";
import Swal from "sweetalert2";
import moment from "moment";
import InvoiceNumberComponent from "../Format";
import ExcelJS from "exceljs"; // Import exceljs for Excel

const Dashboard = (props) => {
  props.setShow(true);
  const userToken = localStorage.getItem("userToken");
  const navigate = useNavigate();

  const initialFilters = {
    filter: "",
    asc: "no",
    fromDate: "",
    toDate: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchInvoices = useCallback(async () => {
    if (!userToken) {
      setError("Authentication required.");
      setInvoices([]);
      setPagination({ total: 0, totalPages: 1 });
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}filterinvoices`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          params: {
            filter: activeFilters.filter,
            asc: activeFilters.asc,
            fromDate: activeFilters.fromDate,
            toDate: activeFilters.toDate,
            page,
            limit,
          },
        }
      );

      const fetchedInvoices = response.data?.invoices ?? [];
      const paginationMeta = response.data?.pagination ?? {};

      const totalPages = Math.max(paginationMeta.totalPages || 1, 1);
      const total = paginationMeta.total ?? fetchedInvoices.length;

      if (page > totalPages && totalPages > 0) {
        setPage(totalPages);
        setPagination({
          total,
          totalPages,
        });
        return;
      }

      setInvoices(fetchedInvoices);
      setPagination({
        total,
        totalPages,
      });
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setError("Unable to fetch invoices. Please try again.");
      setInvoices([]);
      setPagination({ total: 0, totalPages: 1 });
    } finally {
      setIsLoading(false);
    }
  }, [activeFilters, limit, page, userToken]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleDelete = (invoiceId) => {
    const config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_HOST}invoice/${invoiceId}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        if (response.data.status) {
          Swal.fire({
            icon: "success",
            title: "Invoice Deleted Successfully",
            showConfirmButton: false,
            timer: 1500,
          });
          fetchInvoices();
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Failed to Delete Invoice",
          showConfirmButton: false,
          timer: 1500,
        });
      });
  };

  const handleView = (invoiceId) => {
    const url = `/invoice/${invoiceId}/view`;
    window.open(url, "_blank");
  };

  const handleAddInvoice = () => {
    navigate("/create-invoice/new");
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_HOST}invoice/${invoiceId}`,
        config
      );
      const invoiceData = {
        ...response.data.invoice,
        ...response.data.customerDetails,
      };

      const invoiceArray = [];

      // Flatten main invoice data
      Object.entries(invoiceData).forEach(([key, value]) => {
        if (key !== "productDesc") {
          // Skip productDesc for now
          invoiceArray.push({ Field: key, Value: value });
        }
      });

      // Handle productDesc array
      if (invoiceData.productDesc && Array.isArray(invoiceData.productDesc)) {
        invoiceData.productDesc.forEach((product, index) => {
          invoiceArray.push({
            Field: `Product ${index + 1} - Name`,
            Value: product.name,
          });
          invoiceArray.push({
            Field: `Product ${index + 1} - Quantity`,
            Value: product.qty,
          });
          invoiceArray.push({
            Field: `Product ${index + 1} - Price`,
            Value: product.price,
          });
          invoiceArray.push({
            Field: `Product ${index + 1} - UOM`,
            Value: product.uom,
          });
          invoiceArray.push({
            Field: `Product ${index + 1} - HSN`,
            Value: product.hsn,
          });
        });
      }

      // Create workbook and worksheet using exceljs
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Invoice");

      // Add header row
      worksheet.columns = [
        { header: "Field", key: "Field", width: 30 },
        { header: "Value", key: "Value", width: 40 },
      ];

      // Add data rows
      invoiceArray.forEach((row) => {
        worksheet.addRow(row);
      });

      // Creating Excel file and triggering download
      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `Invoice_${invoiceData.billNumber}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Download Invoice",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const handleQuickFilterChange = (event) => {
    const value = event.target.value;
    const updatedFilters = { ...filters, filter: value };
    setPage(1);
    setFilters(updatedFilters);
    setActiveFilters({ ...updatedFilters });
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    const updatedFilters = { ...filters, asc: value };
    setPage(1);
    setFilters(updatedFilters);
    setActiveFilters({ ...updatedFilters });
  };

  const handleSearch = () => {
    setPage(1);
    setActiveFilters({ ...filters });
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(1);
    setLimit(newLimit);
  };

  const handleResetDates = () => {
    if (!filters.fromDate && !filters.toDate) return;
    const resetFilters = { ...filters, fromDate: "", toDate: "" };
    setPage(1);
    setFilters(resetFilters);
    setActiveFilters({ ...resetFilters });
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages) {
      return;
    }
    setPage(nextPage);
  };

  return (
    <div className="container-right">
      <div className="container mt-5 mt-0 d-flex flex-column">
        <div className="filter">
          <select
            value={filters.filter}
            onChange={handleQuickFilterChange}
          >
            <option value={""}>All</option>
            <option value={"todays"}>Today's</option>
            <option value={"last3months"}>Last 3 months</option>
            <option value={"last6months"}>Last 6 months</option>
            <option value={"last12months"}>Last 12 months</option>
            <option value={"draft"}>Draft</option>
          </select>
          <select
            value={filters.asc}
            onChange={handleSortChange}
          >
            <option value={"yes"}>Ascending</option>
            <option value={"no"}>Descending</option>
          </select>
          <br />
          <div>
            <input
              type="date"
              placeholder="Select Start Date"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters({ ...filters, fromDate: e.target.value })
              }
            />
            <input
              type="date"
              placeholder="Select End Date"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
            />
            <button type="button" onClick={handleSearch} disabled={isLoading}>
              Search
            </button>
            <button
              type="button"
              onClick={handleResetDates}
              disabled={isLoading || (!filters.fromDate && !filters.toDate)}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <button className="btn btn-dark new" onClick={handleAddInvoice}>
            + New
          </button>
        </div>
        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}
        <table className="table table-striped">
          <thead>
            <tr>
              <th scope="col">
                <input type="checkbox" />
              </th>
              <th scope="col">Date</th>
              <th scope="col">Invoice#</th>
              <th scope="col">Organisation Name</th>
              <th scope="col">Status</th>
              <th scope="col">Amount</th>
              <th scope="col">Payment Status</th>
              <th scope="col">Manage</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  Loading invoices...
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No invoices found.
                </td>
              </tr>
            ) : (
              invoices.map((invoice, index) => (
                <tr
                  key={invoice._id}
                  className={
                    index % 2 === 0 ? "bg-light" : "bg-secondary text-black"
                  }
                >
                  <td>
                    <input type="checkbox" />
                  </td>
                  <td>
                    {invoice.createdDate
                      ? moment(invoice.createdDate).format("DD-MM-YYYY")
                      : "-"}
                  </td>
                  <td>
                    <InvoiceNumberComponent
                      page={invoice?.invType === "Proforma" ? "P_INV" : "INV"}
                      billNumber={invoice.billNumber}
                      createdDate={invoice.createdDate}
                      lastInvoice={invoice._id}
                    />
                  </td>
                  <td>{invoice.orgName || "-"}</td>
                  <td>{invoice.invoiceStatus || "-"}</td>
                  <td>₹ {Number(invoice.totalINR || 0).toFixed(2)}</td>
                  <td>{invoice.paymentStatus || "-"}</td>
                  <td>
                    <DropdownButton
                      id={`invoice-actions-${invoice._id}`}
                      variant="dark"
                      title={<BsThreeDots size={26} />}
                    >
                      <Dropdown.Item
                        as="button"
                        onClick={() => handleView(invoice._id)}
                      >
                        View
                      </Dropdown.Item>
                      <Dropdown.Item
                        as="button"
                        onClick={() => navigate(`/invoice/edit/${invoice._id}`)}
                      >
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item
                        as="button"
                        onClick={() => handleDelete(invoice._id)}
                      >
                        Delete
                      </Dropdown.Item>
                      <Dropdown.Item
                        as="button"
                        onClick={() => handleDownloadInvoice(invoice._id)}
                      >
                        Download Excel <FaFileExcel />
                      </Dropdown.Item>
                    </DropdownButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">
          <div>
            Showing {isLoading ? 0 : invoices.length} of {pagination.total} invoices
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handlePageChange(page - 1)}
              disabled={isLoading || page <= 1}
            >
              Previous
            </button>
            <span>
              Page {page} of {pagination.totalPages}
            </span>
            <button
              className="btn btn-outline-secondary"
              onClick={() => handlePageChange(page + 1)}
              disabled={isLoading || page >= pagination.totalPages}
            >
              Next
            </button>
            <select
              className="form-select"
              value={limit}
              onChange={handleLimitChange}
              disabled={isLoading}
              style={{ width: "auto" }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
